/**
 * HeartImmersion — Heart Flow immersion experience
 * 
 * 3 silent breaths → seamless transition → Heart glow begins → 7 min immersion → Dissolve
 * 
 * Emotional Arc:
 *   Minute 0–1 (0-60s):   Arrival — very soft glow, "softly… gently…"
 *   Minute 1–3 (60-180s):  Expansion — light grows, "opening… allowing…"
 *   Minute 3–5 (180-300s): Warmth peak — glow stronger, "held… supported…"
 *   Minute 5–7 (300-420s): Integration — light stabilises, "this is yours… this remains…"
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX, SkipForward } from 'lucide-react';
import HeartVisualEngine from './HeartVisualEngine';
import { voiceVolume, binauralVolume, getStoredAudioMode } from '../../utils/modeAudio';
import { captureAndSendKeepsake } from '../../utils/familyKeepsake';

const API = process.env.REACT_APP_BACKEND_URL;

const TOTAL_DURATION = 420;
const DISSOLVE_START = 395;

// Breath cycle — tuned to match the healing_breath.mp3 recording's
// natural cadence (~18.3s per cycle × 3 ≈ 55s) so on-screen prompts
// stay in lock-step with the audible breath.
const BREATH_INHALE = 5000;
const BREATH_HOLD = 3000;
const BREATH_EXHALE = 8000;
const BREATH_REST = 2500;
const TOTAL_BREATHS = 3;

// Emotional arc text sync — matched to the 4-phase structure
const TEXT_SYNC = [
  // Minute 0–1: Arrival
  { t: 3.0, text: 'softly\u2026', dur: 3000 },
  { t: 10.0, text: 'gently\u2026', dur: 3000 },
  { t: 20.0, text: 'a warm light\u2026', dur: 3500 },
  { t: 35.0, text: 'just here\u2026', dur: 3000 },
  { t: 50.0, text: 'softening\u2026', dur: 3000 },
  // Minute 1–3: Expansion
  { t: 68.0, text: 'opening\u2026', dur: 3000 },
  { t: 85.0, text: 'allowing\u2026', dur: 3000 },
  { t: 105.0, text: 'expanding\u2026', dur: 3000 },
  { t: 125.0, text: 'gently widening\u2026', dur: 3500 },
  { t: 150.0, text: 'effortlessly\u2026', dur: 3000 },
  // Minute 3–5: Warmth peak
  { t: 185.0, text: 'held\u2026', dur: 3000 },
  { t: 210.0, text: 'supported\u2026', dur: 3000 },
  { t: 235.0, text: 'warmth\u2026', dur: 3000 },
  { t: 260.0, text: 'safe to soften here\u2026', dur: 4000 },
  { t: 285.0, text: 'deeply held\u2026', dur: 3000 },
  // Minute 5–7: Integration
  { t: 310.0, text: 'this is yours\u2026', dur: 4000 },
  { t: 335.0, text: 'this remains\u2026', dur: 3500 },
  { t: 360.0, text: 'carry this warmth\u2026', dur: 4000 },
  { t: 385.0, text: 'rest\u2026', dur: 5000 },
];

const SUBLIMINAL_FALLBACK = ['warmth', 'safe', 'soften', 'open', 'held', 'loved'];

const HeartImmersion = ({ affirmations = [], onComplete, voiceOverride = null, autoStart = false, keepsakeMeta = null }) => {
  const [mode, setMode] = useState(autoStart ? 'breathing' : 'waiting');
  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  const [dissolveOpacity, setDissolveOpacity] = useState(0);

  const [breathCount, setBreathCount] = useState(0);
  const [breathLabel, setBreathLabel] = useState('');
  const [breathScale, setBreathScale] = useState(1);
  const breathTimerRef = useRef(null);

  const [overlayText, setOverlayText] = useState(null);
  const shownTextsRef = useRef(new Set());

  const [subliminalText, setSubliminalText] = useState('');
  const subliminalTimerRef = useRef(null);
  const subliminalIndexRef = useRef(0);

  const elapsedRef = useRef(0);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const ambientRef = useRef(null);
  const binauralRef = useRef(null);
  const breathRef = useRef(null);
  const keepsakeCapturedRef = useRef(false);

  // Prime the audio elements. Called synchronously from a user tap so
  // iOS Safari treats every element as unlocked. Starts the breath
  // guide immediately so the on-screen "Breathe in / Hold / Slowly
  // release" prompts sync with the audible breath from the very first
  // cycle.
  const primeAudio = useCallback(() => {
    const voiceUrl = voiceOverride?.url
      ? voiceOverride.url
      : `${API}/api/audio/healing/heart_flow_michael.mp3`;
    audioRef.current = new Audio(voiceUrl);
    audioRef.current.volume = 0;
    audioRef.current.load();
    // Breath guidance — reuse the audio that FamilyCircleTile already
    // primed inside the user's tap gesture. iOS Safari only grants the
    // audio-unlock for the current tick, so we CANNOT rely on
    // .play() from inside this useEffect for the very first inhale.
    if (window.__reclaimPrimedBreath) {
      breathRef.current = window.__reclaimPrimedBreath;
      // Fresh start from 0 so words line up with the recording.
      try { breathRef.current.currentTime = 0; } catch (_) { /* noop */ }
      breathRef.current.loop = true;
      breathRef.current.volume = 0.55;
      if (breathRef.current.paused) {
        breathRef.current.play().catch(() => { /* noop */ });
      }
    } else {
      breathRef.current = new Audio(`${API}/api/audio/healing/healing_breath.mp3?v=20260225clean`);
      breathRef.current.loop = true;
      breathRef.current.volume = 0.55;
      breathRef.current.load();
      breathRef.current.play().catch(() => { /* iOS will accept next tap */ });
    }
    try {
      binauralRef.current = new Audio(`${API}/api/audio/binaural/theta_6hz_healing_20min.mp3`);
      binauralRef.current.loop = true;
      binauralRef.current.volume = 0;
      binauralRef.current.load();
    } catch (e) { /* noop */ }
  }, [voiceOverride]);

  const formatMins = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // Breathing sequence
  const runBreathCycle = useCallback((count) => {
    if (count >= TOTAL_BREATHS) {
      setBreathLabel('');
      setBreathScale(1);
      // Seamless transition — brief pause then immersion
      setTimeout(() => setMode('immersion'), 1500);
      return;
    }
    setBreathLabel('Breathe in');
    setBreathScale(1.15);
    breathTimerRef.current = setTimeout(() => {
      setBreathLabel('Hold');
      breathTimerRef.current = setTimeout(() => {
        setBreathLabel('Slowly release');
        setBreathScale(1);
        breathTimerRef.current = setTimeout(() => {
          setBreathLabel('');
          breathTimerRef.current = setTimeout(() => {
            setBreathCount(count + 1);
            runBreathCycle(count + 1);
          }, BREATH_REST);
        }, BREATH_EXHALE);
      }, BREATH_HOLD);
    }, BREATH_INHALE);
  }, []);

  // Start on tap
  const handleTap = useCallback(() => {
    if (mode !== 'waiting') return;
    primeAudio();
    setMode('breathing');
    runBreathCycle(0);
  }, [mode, runBreathCycle, primeAudio]);

  // Auto-start (public one-tap pages): the caller has already handled
  // the user gesture, so we prime audio + kick off the breath cycle on
  // mount. Must be sync inside the mount effect for iOS unlock to hold.
  useEffect(() => {
    if (!autoStart) return;
    primeAudio();
    runBreathCycle(0);
  }, []);

  // Start the binaural/music bed the moment breathing begins so the
  // settle-in is already carried by music + theta. The main healing
  // voice stays off — it only enters at immersion, giving the user a
  // clean "the healing begins now" moment.
  useEffect(() => {
    if (mode !== 'breathing') return;
    if (!binauralRef.current) return;
    // Soft target during breathing — binaural is a subtle floor under
    // the breath guide, not the star. It'll rise to full "loud music"
    // level when the voice arrives at immersion.
    const BREATHING_BED = 0.18;
    binauralRef.current.play().then(() => {
      let vol = 0;
      const fade = setInterval(() => {
        vol += 0.006;
        if (vol >= BREATHING_BED || !binauralRef.current || mode !== 'breathing') {
          clearInterval(fade);
          if (binauralRef.current) binauralRef.current.volume = Math.min(vol, BREATHING_BED);
        } else if (binauralRef.current) {
          binauralRef.current.volume = vol;
        }
      }, 120);
    }).catch(() => {});
  }, [mode]);

  // Immersion timer
  useEffect(() => {
    if (mode !== 'immersion') return;

    // Resolve Clear / Soft / Deep volumes
    const audioMode = getStoredAudioMode();
    const VOICE_TARGET    = voiceVolume(audioMode);
    // "Quite loud music" — Nanna/Grandad pages boost the binaural
    // healing bed roughly 2× the standard app level so the theta pulse
    // is clearly audible as background music, not a whisper.
    const BINAURAL_TARGET = Math.min(0.55, binauralVolume(audioMode) * 2);

    // Breath guide is already playing (started in primeAudio during
    // the settle-in). Just hold it at target here — do NOT reset to 0
    // or we'd cut off mid-cycle.

    // Binaural bed is already running quietly (0.18) from the breathing
    // phase — smoothly RAISE it to its full "loud music" target as the
    // healing voice arrives. Never reset to 0.
    if (binauralRef.current) {
      const startVol = binauralRef.current.volume || 0.18;
      if (binauralRef.current.paused) {
        binauralRef.current.play().catch(() => {});
      }
      let vol = startVol;
      const rise = setInterval(() => {
        vol += 0.008;
        if (vol >= BINAURAL_TARGET || !binauralRef.current) {
          clearInterval(rise);
          if (binauralRef.current) binauralRef.current.volume = BINAURAL_TARGET;
        } else if (binauralRef.current) {
          binauralRef.current.volume = vol;
        }
      }, 100);
    }

    // Start Michael's heart flow voice — fade in after a slightly
    // longer delay so the breathing ceremony ends cleanly before the
    // voice enters (avoids overlap with the last "Slowly release").
    setTimeout(() => {
      if (audioRef.current && audioRef.current.src) {
        audioRef.current.play().then(() => {
          let vol = 0;
          const fade = setInterval(() => {
            vol += 0.015;
            if (vol >= VOICE_TARGET || !audioRef.current) {
              clearInterval(fade);
              if (audioRef.current) audioRef.current.volume = VOICE_TARGET;
            } else if (audioRef.current) {
              audioRef.current.volume = vol;
            }
          }, 60);
        }).catch(() => {});
      }
    }, 2500);

    const start = Date.now();
    timerRef.current = setInterval(() => {
      const s = (Date.now() - start) / 1000;
      elapsedRef.current = s;
      setElapsed(s);
      if (s >= DISSOLVE_START) {
        setDissolveOpacity(Math.min(1, (s - DISSOLVE_START) / (TOTAL_DURATION - DISSOLVE_START)));
        // Capture keepsake at the peak — start of dissolve, before the
        // fade-out has consumed the visual. Best-effort, fire-and-forget.
        if (keepsakeMeta && !keepsakeCapturedRef.current) {
          keepsakeCapturedRef.current = true;
          captureAndSendKeepsake(keepsakeMeta);
        }
        if (binauralRef.current && binauralRef.current.volume > 0.002) {
          binauralRef.current.volume = Math.max(0, binauralRef.current.volume - 0.002);
        }
        if (audioRef.current && audioRef.current.volume > 0.005) {
          audioRef.current.volume = Math.max(0, audioRef.current.volume - 0.005);
        }
        if (breathRef.current && breathRef.current.volume > 0.002) {
          breathRef.current.volume = Math.max(0, breathRef.current.volume - 0.002);
        }
      }
      if (s >= TOTAL_DURATION) {
        clearInterval(timerRef.current);
        if (binauralRef.current) { binauralRef.current.pause(); binauralRef.current = null; }
        if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
        if (breathRef.current) { breathRef.current.pause(); breathRef.current = null; }
        setMode('done');
        setTimeout(onComplete, 2000);
      }
    }, 200);
    setShowSkip(true);

    // Start subliminal cycling
    const subWords = affirmations.length > 0 ? affirmations : SUBLIMINAL_FALLBACK;
    subliminalTimerRef.current = setInterval(() => {
      const idx = subliminalIndexRef.current % subWords.length;
      setSubliminalText(subWords[idx]);
      subliminalIndexRef.current++;
      setTimeout(() => setSubliminalText(''), 3500);
    }, 5000);

    return () => {
      clearInterval(timerRef.current);
      clearInterval(subliminalTimerRef.current);
    };
  }, [mode, affirmations, onComplete]);

  // Text sync
  useEffect(() => {
    if (mode !== 'immersion') return;
    const check = setInterval(() => {
      const s = elapsedRef.current;
      for (const entry of TEXT_SYNC) {
        if (s >= entry.t && s < entry.t + entry.dur / 1000 && !shownTextsRef.current.has(entry.t)) {
          shownTextsRef.current.add(entry.t);
          setOverlayText(entry.text);
          setTimeout(() => setOverlayText(null), entry.dur);
          break;
        }
      }
    }, 300);
    return () => clearInterval(check);
  }, [mode]);

  // Sync mute state across all audio layers
  useEffect(() => {
    if (binauralRef.current) binauralRef.current.muted = muted;
    if (audioRef.current) audioRef.current.muted = muted;
    if (breathRef.current) breathRef.current.muted = muted;
  }, [muted]);

  // Cleanup
  useEffect(() => {
    return () => {
      clearTimeout(breathTimerRef.current);
      clearInterval(timerRef.current);
      clearInterval(subliminalTimerRef.current);
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (ambientRef.current) { ambientRef.current.pause(); ambientRef.current = null; }
      if (binauralRef.current) { binauralRef.current.pause(); binauralRef.current = null; }
      if (breathRef.current) { breathRef.current.pause(); breathRef.current = null; }
    };
  }, []);

  const handleSkip = () => {
    clearInterval(timerRef.current);
    clearInterval(subliminalTimerRef.current);
    if (binauralRef.current) { binauralRef.current.pause(); binauralRef.current = null; }
    if (audioRef.current)    { audioRef.current.pause(); audioRef.current = null; }
    if (breathRef.current)   { breathRef.current.pause(); breathRef.current = null; }
    setMode('done');
    setTimeout(onComplete, 500);
  };

  return (
    <motion.div
      className="fixed inset-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2 }}
      data-testid="heart-immersion"
    >
      {/* Dark warm base background — always present */}
      <div className="fixed inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 42%, rgba(20,14,8,1) 0%, rgba(8,5,3,1) 60%)',
      }} />

      {/* Visual Engine — ONLY during immersion (after breaths complete) */}
      {mode === 'immersion' && (
        <motion.div
          className="fixed inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 3 }}
        >
          <HeartVisualEngine
            elapsed={elapsed}
            totalDuration={TOTAL_DURATION}
            isActive={true}
          />
        </motion.div>
      )}

      {/* Dissolve overlay */}
      {dissolveOpacity > 0 && (
        <motion.div
          className="fixed inset-0 pointer-events-none"
          style={{ background: 'rgba(10,7,4,1)' }}
          animate={{ opacity: dissolveOpacity }}
        />
      )}

      {/* Waiting — Enter */}
      <AnimatePresence>
        {mode === 'waiting' && (
          <motion.div
            className="fixed inset-0 flex flex-col items-center justify-center z-20 cursor-pointer"
            onClick={handleTap}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            data-testid="heart-tap-begin"
          >
            <motion.h2
              className="text-2xl text-white/50 mb-8"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
            >
              Heart Flow
            </motion.h2>
            <motion.p
              className="text-white/35 text-base"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
              animate={{ opacity: [0.25, 0.5, 0.25] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              Enter
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Breathing phase — clean, no heart glow */}
      <AnimatePresence>
        {mode === 'breathing' && (
          <motion.div
            className="fixed inset-0 flex flex-col items-center justify-center z-20 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          >
            <p className="text-white/20 text-xs mb-6 tracking-widest uppercase" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Settle in
            </p>
            <motion.div
              className="rounded-full"
              style={{
                width: 80, height: 80,
                border: '1px solid rgba(210,175,130,0.15)',
                background: 'radial-gradient(circle, rgba(210,175,130,0.06) 0%, transparent 60%)',
              }}
              animate={{ scale: breathScale }}
              transition={{ duration: breathScale > 1 ? BREATH_INHALE / 1000 : BREATH_EXHALE / 1000, ease: 'easeInOut' }}
            />
            {breathLabel && (
              <motion.p
                className="text-white/40 text-sm mt-6"
                style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                key={breathLabel}
              >
                {breathLabel}
              </motion.p>
            )}
            <p className="text-white/15 text-xs mt-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {breathCount + 1} of {TOTAL_BREATHS}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Immersion overlay text */}
      {mode === 'immersion' && (
        <div className="fixed inset-0 flex flex-col items-center justify-end pb-32 z-10 pointer-events-none">
          <AnimatePresence mode="wait">
            {overlayText && (
              <motion.p
                key={overlayText}
                className="text-white/40 text-lg text-center px-10"
                style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 1.5 }}
              >
                {overlayText}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Subliminal text */}
          <AnimatePresence>
            {subliminalText && (
              <motion.p
                key={subliminalText}
                className="text-white text-xs mt-4 text-center"
                style={{ fontFamily: "'Manrope', sans-serif", opacity: 0.04 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.04 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 2 }}
              >
                {subliminalText}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Controls */}
      {mode === 'immersion' && (
        <div className="fixed bottom-6 left-0 right-0 flex items-center justify-between px-6 z-20">
          <p className="text-white/15 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>{formatMins(elapsed)}</p>
          <div className="flex items-center gap-4">
            <button onClick={() => setMuted(!muted)} className="p-2 text-white/20" data-testid="heart-mute-btn">
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            {showSkip && (
              <button onClick={handleSkip} className="p-2 text-white/20" data-testid="heart-skip-btn">
                <SkipForward size={16} />
              </button>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default HeartImmersion;
